import { useEffect } from "react";

/**
 * SEO component — sets document.title, meta description, canonical, OG tags,
 * and injects JSON-LD schema scripts into <head>. Cleans up on unmount.
 *
 * Props:
 *  - title (string)
 *  - description (string)
 *  - canonical (string, path-relative or absolute)
 *  - schemas (array of plain JS objects to JSON.stringify into ld+json)
 *  - ogImage (string, absolute URL)
 */
const SITE_ORIGIN = "https://drindreshdixit.com";

function setMeta(name, content, attr = "name") {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
    created = true;
  }
  el.setAttribute("content", content);
  return { el, created };
}

function setLink(rel, href) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  let created = false;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
    created = true;
  }
  el.setAttribute("href", href);
  return { el, created };
}

export default function SEO({
  title,
  description,
  canonical,
  schemas = [],
  ogImage = "https://customer-assets.emergentagent.com/job_varanasi-gastro/artifacts/jkchhm21_images%20%282%29.jpeg",
  noindex = false,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const created = [];

    const tracked = (r) => {
      if (r?.created) created.push(r.el);
    };

    tracked(setMeta("description", description));
    tracked(setMeta("robots", noindex ? "noindex, follow" : "index, follow"));
    tracked(setMeta("og:title", title, "property"));
    tracked(setMeta("og:description", description, "property"));
    tracked(setMeta("og:type", "website", "property"));
    tracked(
      setMeta(
        "og:url",
        canonical?.startsWith("http")
          ? canonical
          : `${SITE_ORIGIN}${canonical || ""}`,
        "property"
      )
    );
    tracked(setMeta("og:image", ogImage, "property"));
    tracked(setMeta("twitter:card", "summary_large_image"));
    tracked(setMeta("twitter:title", title));
    tracked(setMeta("twitter:description", description));
    tracked(setMeta("twitter:image", ogImage));

    if (canonical) {
      const r = setLink(
        "canonical",
        canonical.startsWith("http") ? canonical : `${SITE_ORIGIN}${canonical}`
      );
      if (r?.created) created.push(r.el);
    }

    // JSON-LD
    const scripts = [];
    schemas.filter(Boolean).forEach((s, i) => {
      const sc = document.createElement("script");
      sc.type = "application/ld+json";
      sc.dataset.seo = "1";
      sc.text = JSON.stringify(s);
      document.head.appendChild(sc);
      scripts.push(sc);
    });

    return () => {
      document.title = prevTitle;
      scripts.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
      // Note: we don't remove meta tags we created/modified on cleanup —
      // the next page's SEO component will overwrite them on mount.
    };
  }, [title, description, canonical, JSON.stringify(schemas), ogImage, noindex]);

  return null;
}
